import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatNaira } from '@/lib/utils';
import { CaretLeft, ChartBar, Users, Briefcase, CurrencyDollar, TrendUp, Medal } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from 'recharts';

const COLORS = ['#1A4D2E', '#E86A33', '#2D6A4F', '#F4A261', '#5C5C5C', '#D35A29', '#133922', '#A0522D', '#2C3E50', '#E67E22', '#16A085', '#8E44AD'];

export default function RevenueDashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try { const { data: d } = await api.get('/admin/revenue'); setData(d); } catch {} finally { setLoading(false); }
    };
    load();
  }, [api]);

  if (loading) return <div className="min-h-screen bg-[#E8DDD2] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#E8DDD2] pb-10 max-w-[540px] mx-auto" data-testid="revenue-dashboard-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">Revenue Dashboard</h1>
        <TrendUp size={20} className="text-[#2D6A4F]" />
      </header>

      <div className="p-4 space-y-4">
        {/* Hero Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gradient-to-br from-[#1A4D2E] to-[#133922] rounded-xl p-4 text-white relative overflow-hidden">
            <div className="absolute top-[-20px] right-[-20px] w-[80px] h-[80px] bg-white/5 rounded-full" />
            <CurrencyDollar size={20} className="text-white/50 mb-1" />
            <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">Total Revenue</p>
            <p className="font-heading text-xl font-black" data-testid="total-revenue">{formatNaira(data.total_revenue)}</p>
          </div>
          <div className="bg-gradient-to-br from-[#E86A33] to-[#D35A29] rounded-xl p-4 text-white relative overflow-hidden">
            <div className="absolute top-[-20px] right-[-20px] w-[80px] h-[80px] bg-white/5 rounded-full" />
            <ChartBar size={20} className="text-white/50 mb-1" />
            <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">Escrow Held</p>
            <p className="font-heading text-xl font-black">{formatNaira(data.total_escrow_held)}</p>
          </div>
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C]">Paid Out</p>
            <p className="font-heading text-xl font-bold text-[#2D6A4F]">{formatNaira(data.total_paid_out)}</p>
          </div>
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C]">Reg Fees</p>
            <p className="font-heading text-xl font-bold text-[#F4A261]">{data.reg_fees_collected}</p>
          </div>
        </div>

        {/* Monthly Revenue Chart */}
        {data.monthly_revenue?.length > 0 && (
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-4" data-testid="monthly-chart">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-3">Monthly Revenue</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.monthly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#5C5C5C' }} />
                <YAxis tick={{ fontSize: 10, fill: '#5C5C5C' }} tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatNaira(v)} labelStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#1A4D2E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Jobs by Category */}
        {data.jobs_by_category?.length > 0 && (
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-4" data-testid="category-chart">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-3">Jobs by Category</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.jobs_by_category} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name?.replace(/_/g,' ')} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={9}>
                  {data.jobs_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Jobs by Status */}
        {data.jobs_by_status?.length > 0 && (
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-4" data-testid="status-chart">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-3">Jobs by Status</h3>
            <div className="grid grid-cols-2 gap-2">
              {data.jobs_by_status.map((s, i) => (
                <div key={s.status} className="flex items-center gap-2 p-2 rounded-lg bg-[#E8DDD2]">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-xs font-body text-[#5C5C5C] flex-1">{s.status?.replace(/_/g, ' ')}</span>
                  <span className="text-xs font-bold text-[#1C1C1C]">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Artisans Leaderboard */}
        {data.top_artisans?.length > 0 && (
          <div className="bg-white border border-[#D4C5B3] rounded-xl overflow-hidden" data-testid="leaderboard">
            <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] px-4 py-3 flex items-center gap-2">
              <Medal size={18} className="text-[#F4A261]" />
              <span className="text-white font-bold text-sm font-body">Top Artisans</span>
            </div>
            <div className="divide-y divide-[#E5E3DB]">
              {data.top_artisans.map((a, i) => (
                <div key={a.id} className="flex items-center gap-3 px-4 py-3" data-testid={`leaderboard-${i}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < 3 ? 'bg-[#F4A261] text-white' : 'bg-[#E8DDD2] text-[#5C5C5C]'}`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1C1C1C] font-body truncate">{a.name}</p>
                    <p className="text-[10px] text-[#5C5C5C]">{a.trade || a.role} · {a.jobs_completed || 0} jobs · ★{a.star_rating?.toFixed(1)}</p>
                  </div>
                  <span className="font-heading text-sm font-bold text-[#E86A33]">{formatNaira(a.total_earned)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
